// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

/**
 * On-device cache for satellite tiles.
 *
 * Why this exists: the hole view fetches 6–20 tiles per opening, and zooming multiplies that
 * because every zoom level is a different tile set. Without a cache a player walking a round
 * re-downloads the same imagery hole after hole — on mobile data, on a course, where the
 * signal is worst. Tiles are also immutable for their zoom/x/y, so they are about the most
 * cacheable thing there is.
 *
 * The browser already does this on web, so this is native-only and no-ops elsewhere.
 *
 * The store is injected rather than imported so the eviction and keying logic — the part
 * that can lose a player's imagery or fill their phone — is tested directly.
 */

import { Platform } from 'react-native';

/** The filesystem operations the cache needs, so tests can supply their own. */
export type TileStore = {
  /** Local URI for a cached tile, or null when it is not held. */
  read: (key: string) => Promise<string | null>;
  /** Fetches `url` into the cache and returns the local URI. */
  write: (key: string, url: string) => Promise<string>;
  /** Every held key with its size in bytes and last use, oldest use first. */
  list: () => Promise<Array<{ key: string; size: number; usedAt: number }>>;
  remove: (key: string) => Promise<void>;
};

/**
 * Roughly 2,000 tiles at 30 KB. Comfortably a season of courses, and small enough that no
 * player will notice it in their storage settings.
 */
export const MAX_CACHE_BYTES = 60 * 1024 * 1024;

/** Evicting one tile at a time would run the sweep constantly; this clears real headroom. */
const EVICT_TO_FRACTION = 0.8;

/** A tile URL reduced to a filename-safe key. Same tile, same key, whatever the host. */
export const tileCacheKey = (url: string): string =>
  url.replace(/^https?:\/\//, '').replace(/[^a-zA-Z0-9]+/g, '_');

/**
 * Drops least-recently-used entries until the cache is comfortably under the cap.
 *
 * Returns the keys removed, which is what makes the policy assertable without a filesystem.
 */
export const selectEvictions = (
  entries: Array<{ key: string; size: number; usedAt: number }>,
  maxBytes: number = MAX_CACHE_BYTES
): string[] => {
  const total = entries.reduce((sum, entry) => sum + entry.size, 0);
  if (total <= maxBytes) return [];

  const target = maxBytes * EVICT_TO_FRACTION;
  const oldestFirst = [...entries].sort((a, b) => a.usedAt - b.usedAt);
  const evicted: string[] = [];
  let remaining = total;

  for (const entry of oldestFirst) {
    if (remaining <= target) break;
    evicted.push(entry.key);
    remaining -= entry.size;
  }
  return evicted;
};

let store: TileStore | null = null;
let storeResolved = false;

/** Test seam: supply a store, or null to force the uncached path. */
export const __setTileStore = (next: TileStore | null) => {
  store = next;
  storeResolved = true;
};

const resolveStore = (): TileStore | null => {
  if (storeResolved) return store;
  storeResolved = true;
  // The browser's own HTTP cache already covers web, and expo-file-system has no business
  // being loaded there.
  store = Platform.OS === 'web' ? null : createExpoTileStore();
  return store;
};

/**
 * The URI to render a tile from: the cached copy when there is one, the network otherwise.
 *
 * Never throws and never returns nothing — every failure falls back to the remote URL, so a
 * broken cache costs bandwidth rather than a blank map.
 */
export const getTileUri = async (url: string): Promise<string> => {
  const active = resolveStore();
  if (!active) return url;

  const key = tileCacheKey(url);
  try {
    const cached = await active.read(key);
    if (cached) return cached;
  } catch {
    return url;
  }

  try {
    const written = await active.write(key, url);
    void sweep(active);
    return written;
  } catch {
    return url;
  }
};

/** Trims the cache in the background. Failures are silent: this is housekeeping. */
const sweep = async (active: TileStore): Promise<void> => {
  try {
    const entries = await active.list();
    for (const key of selectEvictions(entries)) {
      await active.remove(key);
    }
  } catch {
    // ignore
  }
};

/**
 * expo-file-system adapter.
 *
 * Loaded lazily and defensively: the module is part of the Expo SDK rather than a direct
 * dependency, and a missing or changed API must degrade to "no cache" rather than taking
 * the hole view down with it.
 */
const createExpoTileStore = (): TileStore | null => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const fs = require('expo-file-system') as {
      Paths: { cache: unknown };
      File: new (...parts: unknown[]) => {
        exists: boolean;
        size?: number;
        uri: string;
        delete: () => void;
      } & { downloadFileAsync?: unknown };
      Directory: new (...parts: unknown[]) => {
        exists: boolean;
        create: (options?: { intermediates?: boolean }) => void;
        list: () => Array<{ name?: string; uri: string; size?: number; delete: () => void }>;
      };
    };
    if (!fs?.Paths?.cache || !fs.File || !fs.Directory) return null;

    const dir = new fs.Directory(fs.Paths.cache, 'goffy-tiles');
    if (!dir.exists) dir.create({ intermediates: true });

    const fileFor = (key: string) => new fs.File(dir, key);

    return {
      read: async (key) => {
        const file = fileFor(key);
        return file.exists ? file.uri : null;
      },
      write: async (key, url) => {
        const download = (fs.File as unknown as {
          downloadFileAsync: (from: string, to: unknown) => Promise<{ uri: string }>;
        }).downloadFileAsync;
        const result = await download(url, fileFor(key));
        return result.uri;
      },
      list: async () =>
        dir.list().map((entry) => ({
          key: entry.name ?? entry.uri,
          size: entry.size ?? 0,
          // The API exposes no modification time, so eviction falls back to directory order.
          // Imperfect, but it still bounds the cache, which is the job that matters.
          usedAt: 0,
        })),
      remove: async (key) => {
        const file = fileFor(key);
        if (file.exists) file.delete();
      },
    };
  } catch {
    return null;
  }
};
